/**
 * SQLDisplay Component
 *
 * Displays SQL queries with syntax highlighting and metadata
 * Shows row counts, iteration numbers, retry indicators, and results table
 */

import { Box, Typography, IconButton, Chip, Collapse } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useState } from "react";
import { SQLQuery } from "../../contexts/chat/type";

interface SQLDisplayProps {
  queries: SQLQuery[];
  showResults?: boolean;
}

export const SQLDisplay = ({ queries, showResults = true }: SQLDisplayProps) => {
  // Initialize with all queries expanded
  const [expandedQueries, setExpandedQueries] = useState<Set<number>>(
    () => new Set(queries.map((_, index) => index))
  );

  if (!queries || queries.length === 0) {
    return null;
  }

  const handleCopy = (sql: string | null | undefined) => {
    if (sql) {
      navigator.clipboard.writeText(sql);
    }
  };

  const toggleExpand = (index: number) => {
    setExpandedQueries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}
      >
        SQL Queries Executed
      </Typography>

      {queries.map((query, index) => (
        <Box
          key={index}
          sx={{
            mb: 2,
            border: "1px solid",
            borderColor: "grey.300",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {/* Query Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              backgroundColor: "grey.100",
              borderBottom: "1px solid",
              borderColor: "grey.300",
              cursor: "pointer",
            }}
            onClick={() => toggleExpand(index)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                Query #{index + 1}
              </Typography>
              {queries.length > 1 && (
                <Chip
                  label={`Attempt ${query.iteration}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                icon={
                  query.row_count === 0 ? (
                    <WarningAmberIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                  )
                }
                label={`${query.row_count} rows`}
                size="small"
                color={query.row_count === 0 ? "warning" : "success"}
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Box>
          </Box>

          <Collapse in={expandedQueries.has(index)}>
            {/* SQL Code */}
            <Box sx={{ position: "relative" }}>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  m: 0,
                  backgroundColor: "#1e1e1e",
                  color: "#d4d4d4",
                  fontSize: "0.75rem",
                  fontFamily: "Consolas, Monaco, monospace",
                  overflow: "auto",
                  maxHeight: 200,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {query.sql || "SQL not available"}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy(query.sql);
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "#d4d4d4",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>

            {/* Summary */}
            {query.summary && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: "grey.50",
                  borderTop: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {query.summary.length > 200
                    ? query.summary.substring(0, 200) + "..."
                    : query.summary}
                </Typography>
              </Box>
            )}

            {/* Results Table */}
            {showResults &&
              query.results &&
              query.results.length > 0 &&
              query.columns &&
              query.columns.length > 0 && (
                <Box sx={{ borderTop: "1px solid", borderColor: "grey.200" }}>
                  <Box sx={{ px: 2, py: 1, backgroundColor: "grey.50" }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Results{" "}
                      {query.results.length < query.row_count
                        ? `(showing first ${query.results.length} of ${query.row_count} rows)`
                        : `(${query.row_count} rows)`}
                    </Typography>
                  </Box>
                  <Box sx={{ overflow: "auto", maxHeight: 300 }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "0.75rem",
                      }}
                    >
                      <thead>
                        <tr style={{ backgroundColor: "#f5f5f5" }}>
                          {query.columns.map((column, colIndex) => (
                            <th
                              key={colIndex}
                              style={{
                                padding: "8px 12px",
                                textAlign: "left",
                                fontWeight: 600,
                                borderBottom: "1px solid #e0e0e0",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {column}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {query.results.map((row, rowIndex) => (
                          <tr
                            key={rowIndex}
                            style={{
                              backgroundColor:
                                rowIndex % 2 === 0 ? "#fff" : "#fafafa",
                            }}
                          >
                            {query.columns!.map((column, colIndex) => (
                              <td
                                key={colIndex}
                                style={{
                                  padding: "6px 12px",
                                  borderBottom: "1px solid #e0e0e0",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {row[column] !== null && row[column] !== undefined
                                  ? String(row[column])
                                  : "null"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              )}

            {/* Retry indicator */}
            {query.row_count === 0 && index < queries.length - 1 && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: "warning.light",
                  borderTop: "1px solid",
                  borderColor: "warning.main",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <WarningAmberIcon
                  sx={{ fontSize: 16, color: "warning.dark" }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "warning.dark", fontWeight: 500 }}
                >
                  No results found. Agent retried with adjusted approach.
                </Typography>
              </Box>
            )}
          </Collapse>
        </Box>
      ))}
    </Box>
  );
};
